import { useCallback, useEffect, useRef, useState } from 'react';
import { Activity, AlertOctagon, Bell, FileClock, RefreshCw, ServerCog, ShieldCheck } from 'lucide-react';
import { ApiError, getAudit, getDashboard, getTraceDetail, searchTraces } from './lib/api';
import type { AuditListResponse, DashboardData, TraceDetailView, TraceFilterForm, TraceListItem } from './types';
import { toTraceFilters } from './types';
import { formatTime } from './lib/format';
import { ServiceStatus } from './components/ServiceStatus';
import { TraceFiltersBar } from './components/TraceFiltersBar';
import { TraceList } from './components/TraceList';
import { TraceDetailPanel } from './components/TraceDetailPanel';
import { AuditPanel } from './components/AuditPanel';
import { ModelRequestsPanel } from './components/ModelRequestsPanel';

const emptyFilters: TraceFilterForm = { roomId: '', runId: '', playerId: '', source: '', status: '' };

export default function App() {
  const [view, setView] = useState<'traces' | 'requests' | 'audit'>('traces');
  const [dashboard, setDashboard] = useState<DashboardData>();
  const [filters, setFilters] = useState<TraceFilterForm>(emptyFilters);
  const [selectedId, setSelectedId] = useState<string>();
  const [detail, setDetail] = useState<TraceDetailView>();
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pageError, setPageError] = useState<string>();
  const [detailError, setDetailError] = useState<string>();
  const [audit, setAudit] = useState<AuditListResponse>();
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<{ code?: string; message: string }>();
  const detailRequest = useRef(0);

  const selectTrace = useCallback(async (trace: TraceListItem) => {
    const requestNumber = ++detailRequest.current;
    setSelectedId(trace.traceId);
    setDetailLoading(true);
    setDetailError(undefined);
    try {
      const nextDetail = await getTraceDetail(trace.traceId);
      if (requestNumber === detailRequest.current) setDetail(nextDetail);
    } catch (error) {
      if (requestNumber === detailRequest.current) {
        setDetail(undefined);
        setDetailError(error instanceof Error ? error.message : '未知错误');
      }
    } finally {
      if (requestNumber === detailRequest.current) setDetailLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(undefined);
    try {
      const nextDashboard = await getDashboard();
      setDashboard(nextDashboard);
      const first = nextDashboard.traces.items[0];
      if (first) {
        await selectTrace(first);
      } else {
        setSelectedId(undefined);
        setDetail(undefined);
      }
    } catch (error) {
      setPageError(error instanceof Error ? error.message : '无法连接 GM API');
    } finally {
      setLoading(false);
    }
  }, [selectTrace]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (view === 'audit' && dashboard && !dashboard.viewer.permissions.includes('audit:read')) {
      setView('traces');
    }
  }, [dashboard, view]);

  const runSearch = async (nextFilters = filters) => {
    if (!dashboard) return;
    setSearching(true);
    setPageError(undefined);
    try {
      const traces = await searchTraces(toTraceFilters(nextFilters));
      setDashboard({ ...dashboard, traces });
      const first = traces.items[0];
      if (first) {
        await selectTrace(first);
      } else {
        setSelectedId(undefined);
        setDetail(undefined);
      }
    } catch (error) {
      setPageError(error instanceof Error ? error.message : '检索失败');
    } finally {
      setSearching(false);
    }
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    void runSearch(emptyFilters);
  };

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    setAuditError(undefined);
    try {
      setAudit(await getAudit());
    } catch (error) {
      setAuditError(error instanceof ApiError
        ? { ...(error.code ? { code: error.code } : {}), message: error.message }
        : { message: error instanceof Error ? error.message : '无法读取审计记录' });
    } finally {
      setAuditLoading(false);
    }
  }, []);

  if (loading && !dashboard) {
    return (
      <main className="app-loading" aria-label="正在加载 Maland Control Room">
        <div className="brand-mark brand-mark--large">M</div>
        <div><strong>Maland Control Room</strong><span>正在连接控制平面…</span></div>
        <i />
      </main>
    );
  }

  if (pageError && !dashboard) {
    return (
      <main className="app-error">
        <div className="app-error__icon"><AlertOctagon size={28} /></div>
        <span className="eyebrow">CONTROL PLANE OFFLINE</span>
        <h1>无法载入 GM 工作台</h1>
        <p>{pageError}</p>
        <button className="button button--primary" onClick={() => void load()}><RefreshCw size={15} />重新连接</button>
      </main>
    );
  }

  if (!dashboard) return null;

  const initials = (dashboard.viewer.displayName ?? dashboard.viewer.email).slice(0, 2).toUpperCase();
  const canReadAudit = dashboard.viewer.permissions.includes('audit:read');
  const canReadModelRequests = dashboard.viewer.permissions.includes('ai-detail:read');

  const openAudit = () => {
    setView('audit');
    if (!audit && !auditLoading) void loadAudit();
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark">M</div>
          <div><strong>Maland</strong><span>Control Room</span></div>
        </div>
        <nav aria-label="主导航">
          <button className={`nav-link${view === 'traces' ? ' nav-link--active' : ''}`} onClick={() => setView('traces')}><Activity size={16} />Trace 调查台</button>
          {canReadModelRequests && <button className={`nav-link${view === 'requests' ? ' nav-link--active' : ''}`} onClick={() => setView('requests')}><ServerCog size={16} />HTTP 请求包体</button>}
          {canReadAudit && <button className={`nav-link${view === 'audit' ? ' nav-link--active' : ''}`} onClick={openAudit}><FileClock size={16} />操作审计</button>}
        </nav>
        <div className="topbar__actions">
          <button className="icon-only" aria-label="通知"><Bell size={17} /></button>
          <div className="identity">
            <span className="identity__avatar">{initials}</span>
            <div><strong>{dashboard.viewer.displayName ?? dashboard.viewer.email}</strong><span><ShieldCheck size={12} />{dashboard.viewer.role}</span></div>
          </div>
        </div>
      </header>

      <main className="content" id="trace-workspace">
        <div className="page-heading">
          <div>
            <span className="eyebrow">INTERNAL OPERATIONS</span>
            <h1>{view === 'traces' ? 'AI Trace 调查台' : view === 'requests' ? 'HTTP 请求包体' : '访问与操作审计'}</h1>
            <p>{view === 'traces' ? '按一次玩家运行，对齐 Game Server、AI Backend 与 Unity Client 的真实时序。' : view === 'requests' ? '查看 Go AI Backend 发往模型网关的最近请求、响应、耗时与 Token。' : '追踪谁在何时读取或操作了哪些内部资源，并识别被拒绝和失败的请求。'}</p>
          </div>
          <div className="page-heading__actions">
            {view !== 'requests' && <><span>更新于 {formatTime(dashboard.status.checkedAt)}</span>
              <button className="button button--secondary" onClick={() => void (view === 'traces' ? load() : loadAudit())} disabled={view === 'traces' ? loading : auditLoading}>
                <RefreshCw className={(view === 'traces' ? loading : auditLoading) ? 'spin' : ''} size={15} />刷新
              </button></>}
          </div>
        </div>

        {view === 'traces' && pageError && (
          <div className="banner-error" role="alert"><AlertOctagon size={16} /><span>{pageError}</span><button onClick={() => setPageError(undefined)}>关闭</button></div>
        )}

        {view === 'traces' ? (
          <>
            <ServiceStatus status={dashboard.status} />
            <TraceFiltersBar value={filters} busy={searching} onChange={setFilters} onSubmit={() => void runSearch()} onReset={resetFilters} />
            <div className="workspace">
              <TraceList
                traces={dashboard.traces.items}
                total={dashboard.traces.total}
                selectedId={selectedId}
                busy={searching}
                onSelect={(trace) => void selectTrace(trace)}
              />
              <TraceDetailPanel
                detail={detail}
                busy={detailLoading}
                error={detailError}
                canExport={dashboard.viewer.permissions.includes('trace:export')}
                canReadAiDetails={dashboard.viewer.permissions.includes('ai-detail:read')}
              />
            </div>
          </>
        ) : view === 'requests' && canReadModelRequests ? (
          <ModelRequestsPanel />
        ) : canReadAudit ? (
          <AuditPanel items={audit?.items ?? []} loading={auditLoading} error={auditError} onRetry={() => void loadAudit()} />
        ) : null}
      </main>
      <footer>
        <span>Maland Platform · Read-only Trace Console</span>
        <span>敏感字段由服务端脱敏 · 所有访问均记录审计</span>
      </footer>
    </div>
  );
}
